import Link, { type LinkProps } from 'next/link';

import {
  ToastContainer,
  ToastContent,
  ToastTitle,
} from '@/components/TransactionToast/ToastComponents';

import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';
import { CHAIN_DEPRECATION_END_LABEL, DEPRECATED_CHAIN_IDS } from '@/constants/chainDeprecation';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { linkStyles } from '@/styles/links';
import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { AlertIcon } from '@/ui/Icons/AlertIcon';
import { ChevronRightIcon } from '@/ui/Icons/ChevronRightIcon';

export interface DeprecatedChainBalance {
  chainId: number;
  balanceUsd: number;
  bridgeHref: LinkProps['href'];
}

export interface ChainDeprecationToastProps {
  chains: DeprecatedChainBalance[];
  totalUsd: number;
  primaryChain: DeprecatedChainBalance;
  onBridgeClick: (chainId: number) => void;
  onClose: () => void;
}

const chainListFormat = new Intl.ListFormat('en', { type: 'conjunction' });

export function ChainDeprecationToast({
  chains,
  totalUsd,
  primaryChain,
  onBridgeClick,
  onClose,
}: ChainDeprecationToastProps) {
  const { formatFiat } = useFiatDisplay();

  const deprecatedChainNames = chainListFormat.format(
    DEPRECATED_CHAIN_IDS.map((chainId) => NETWORK_NAME_PER_CHAIN_ID[chainId]),
  );
  const location =
    chains.length === 1
      ? `on ${NETWORK_NAME_PER_CHAIN_ID[chains[0].chainId]}`
      : `across ${chains.length} chains`;

  return (
    <ToastContainer onClose={onClose} showProgressAnimation={false} onHoverChange={() => {}}>
      <div className="flex-shrink-0 relative z-10 size-12 rounded-full bg-token-orange-300 text-token-orange-1100 flex items-center justify-center">
        <AlertIcon className="size-5" />
      </div>

      <div className="flex flex-col gap-1 min-w-0 flex-1 relative z-10 pr-8">
        <ToastTitle
          className={fontStyles({
            variant: 'LABEL_MD',
            class: ['text-token-gray-1600'],
          })}
        >
          {deprecatedChainNames} support ends {CHAIN_DEPRECATION_END_LABEL}
        </ToastTitle>

        <ToastContent>
          <span
            className={fontStyles({
              variant: 'BODY_SM',
              class: ['text-token-gray-800'],
            })}
          >
            Bridge your assets to another chain before then. You have {formatFiat(totalUsd)}{' '}
            {location}.
          </span>
        </ToastContent>

        <div className="flex flex-wrap gap-1.5 mt-1">
          {chains.map(({ chainId, balanceUsd, bridgeHref }) => (
            <Link
              key={chainId}
              href={bridgeHref}
              onClick={() => onBridgeClick(chainId)}
              className={fontStyles({
                variant: 'LABEL_SM',
                class: [
                  'flex',
                  'items-center',
                  'gap-1.5',
                  'rounded-full',
                  'bg-token-gray-200',
                  'hover:bg-token-gray-300',
                  'transition-colors',
                  'py-1',
                  'pl-1',
                  'pr-2.5',
                  'text-token-gray-1600',
                ],
              })}
            >
              <ChainAvatar chainId={chainId} size={16} />
              {NETWORK_NAME_PER_CHAIN_ID[chainId]}
              <span className="text-token-gray-800">{formatFiat(balanceUsd)}</span>
            </Link>
          ))}
        </div>

        <div className="mt-1">
          <Link
            href={primaryChain.bridgeHref}
            onClick={() => onBridgeClick(primaryChain.chainId)}
            className={linkStyles({
              color: 'purple',
              font: 'LABEL_MD',
              class: 'inline-flex items-center gap-1.5',
            })}
          >
            Bridge assets
            <ChevronRightIcon />
          </Link>
        </div>
      </div>
    </ToastContainer>
  );
}
